import React, { useState, useEffect } from 'react';
import {
    List, Datagrid, TextField, FunctionField, SimpleList,
} from 'react-admin';
import {
    useMediaQuery, makeStyles, FormControl, InputLabel, Select, MenuItem, IconButton, Dialog
} from '@material-ui/core';
import { customTheme } from "./customTheme";
import CustomSearch from './components/CustomSearch';
import { useLocation } from 'react-router-dom';
import CustomPagination from './components/CustomPagination';
import ScrollableTable from './components/ScrollableTable';
import UseAnalyticsEvent from './utils/useAnalyticsEvent';
import queryString from 'query-string';
import ParkingSearchSelector from './components/ParkingSearchSelector';
import LiveTvIcon from "@material-ui/icons/LiveTv";
import { Helmet } from "react-helmet";
import BrokenImageOutlinedIcon from '@material-ui/icons/BrokenImageOutlined';
import ImageGalleryDialog from './components/ImageGalleryDialog';

const useStyles = makeStyles({
    list: {
        marginBottom: 0,
    },
    parkingSelectorContainer: {
        display: "grid",
        justifyContent: "space-between",
    },
    fullWidthDialog: {
        maxWidth: 900
    }
});

const CATEGORY = "PURCHASED SECTION"
const SEARCH_TYPES = ["Description"];

export const CamerasList = props => {
    const eventTracker = UseAnalyticsEvent();
    const classes = useStyles();
    const location = useLocation();
    const isSmallDevice = useMediaQuery(theme => customTheme.breakpoints.down("xs"));
    let prevSearch = sessionStorage.getItem(location.pathname) ? JSON.parse(sessionStorage.getItem(location.pathname)) : "";
    const [currentParking, setCurrentParking] = useState(prevSearch.currentParking ? prevSearch.currentParking : {});
    const [showFilter] = useState(sessionStorage.getItem("show_filter") === "1");
    const zoneIds = currentParking.zone_ids ? currentParking.zone_ids.split(",") : null;
    const zoneLabels = currentParking.zones ? currentParking.zones.split(";") : null;
    const finalZones = zoneIds && zoneLabels ? zoneIds.map((id, index) => (
        { id, name: zoneLabels[index] }
    )) : null;
    if (finalZones)
        finalZones.sort((a, b) => (a.name > b.name ? 1 : (a.name < b.name ? -1 : 0)));

    const [search, setSearch] = useState(prevSearch.inputSearch);
    const [searchTypes, setSearchTypes] = useState(prevSearch.type ? prevSearch.type : "Description");

    const filter = { search: { value: search ? search : "", type: searchTypes ? searchTypes : "" }, access_key: currentParking.id, parking_info_id: currentParking.allZones ? currentParking.parking_info_id : undefined };

    const onParkingChange = (parking) => {
        setCurrentParking({ ...parking, allZones: true });
    };

    const onZoneChange = (event) => {
        setCurrentParking({
            ...currentParking,
            id: event.target.value,
            name: zoneLabels[zoneIds.findIndex(id => id === event.target.value)],
            allZones: event.target.value === "All"
        });
    };

    useEffect(() => {
        eventTracker(CATEGORY, "PARKING CAMERAS LIST");
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const queryParams = queryString.parse(props.location.search);
        if (queryParams.parking)
            setCurrentParking(JSON.parse(queryParams.parking));
    }, [props]);

    return <div className={classes.list}>
        <Helmet>
            <script src="https://cdn.jsdelivr.net/npm/hls.js@1"></script>
        </Helmet>

        <div className={classes.parkingSelectorContainer} style={{ gridAutoFlow: isSmallDevice ? "row" : "column", gridTemplateColumns: isSmallDevice ? "100%" : "48% 48%", gridRowGap: isSmallDevice ? 18 : 0, margin: 0 }}>
            {showFilter &&
                <ParkingSearchSelector
                    value={currentParking}
                    onChange={onParkingChange}
                    clearable={true}
                    pathName={location.pathname}
                />
            }

            {currentParking.zone_ids && currentParking.zone_ids.includes(',') &&
                <FormControl variant="outlined" style={{ marginTop: isSmallDevice ? 0 : 10 }}>
                    <InputLabel>Zone Name</InputLabel>
                    <Select
                        value={currentParking.allZones ? "All" : currentParking.id}
                        onChange={onZoneChange}
                        label="Zone Name"
                        variant="outlined"
                    >
                        <MenuItem value="All">All</MenuItem>
                        {finalZones.map((zone, index) => (
                            <MenuItem key={index} value={zone.id}>{zone.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            }
        </div>
        <div className='customStyleForDataSearchDiv' style={{marginBottom:15}}>
            <CustomSearch search={search} setSearch={setSearch} searchTypes={searchTypes} setSearchTypes={setSearchTypes} searchTypeState={SEARCH_TYPES} pathName={location.pathname} />
        </div>
        <List
            {...props}
            title="Parking Cameras"
            bulkActionButtons={false}
            exporter={false}
            filter={filter}
            empty={false}
            pagination={<CustomPagination />}
            actions={false}
        >
            {isSmallDevice ?
                <SimpleList
                    primaryText={record => record.description}
                    secondaryText={record => (<div>
                        <div><span style={{ color: "#5a5a5a" }}>Parking Name: </span>{record.parking_name}</div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            {record && record.latest_image && record.latest_image.split("|")[1] ? <ImageGalleryDialog thumbnail={true} record={record} style={{ textAlign: "left" }} getImages={(record) => {
                                return [record.latest_image.split("|")[1]]
                            }} />
                                : <div style={{ display: "flex", justifyContent: "center", alignItems: "center", border: "1px solid #BDBDBD", minWidth: "50px", width: "60px", borderRadius: 5, height: "40px" }}>
                                    <BrokenImageOutlinedIcon />
                                </div>
                            }
                            <IconButton>
                                <LiveTvIcon style={{ color: "#18cc69" }} />
                            </IconButton>
                        </div>
                    </div>)}
                    onContextMenu={event => event.preventDefault()}
                    linkType={false}
                    rowStyle={() => ({ alignItems: "flex-start", borderBottom: "1px solid #18cc69" })}
                />
            :
                <Datagrid component={ScrollableTable}>
                    <TextField source='description' label="Description" />
                    <TextField source='parking_name' label="Parking" />
                    <FunctionField label="Image" render={(record) => record && record.latest_image && record.latest_image.split("|")[1] ? <ImageGalleryDialog thumbnail={true} record={record} style={{textAlign:"left"}} getImages={(record) => {
                            return [record.latest_image.split("|")[1]]
                        }} /> :
                            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", border: "1px solid #BDBDBD", minWidth: "50px", width: "60px", borderRadius: 5, height: "40px" }}>
                                <BrokenImageOutlinedIcon />
                            </div>
                        } />
                    <FunctionField render={record => record && <LiveStreamButton record={record} source="live_stream_url" />} />
                </Datagrid>
            }
        </List>
    </div>
};

function LiveStreamButton(props) {
    const classes = useStyles();
    const { record, source } = props;
    const isSmallDevice = useMediaQuery(theme => theme.breakpoints.down("xs"));

    const [dialogVisible, setDialogVisible] = useState(false);
    const [playerReady, setPlayerReady] = useState(false);

    const openDialog = () => {
        setDialogVisible(true);
    };

    const closeDialog = () => {
        setDialogVisible(false);
    };

    const startPlay = () => {
        let url = record && record[source] ? record[source] : "";
        const videoPlayer = document.getElementById(`videoPlayer${record.id}`);
        if (videoPlayer.canPlayType('application/vnd.apple.mpegurl')) {
            videoPlayer.src = url;
            videoPlayer.load();
        } else if (window.Hls.isSupported()) {
            let hls = new window.Hls({ manifestLoadingTimeOut: 60000 });
            hls.loadSource(url);
            hls.attachMedia(videoPlayer);
            hls.on('error', function(e) {
              console.log("HLS Error:", e);
            });
        } else {
            console.log("Your browser doesn't support hls");
        }
    
    }

    useEffect(() => {
        setPlayerReady(dialogVisible);
    }, [dialogVisible]);

    useEffect(() => {
        if(record[source]) {
            const videoPlayer = document.getElementById(`videoPlayer${record.id}`);
            if(videoPlayer) {
                let canplaythroughTime = null;
                videoPlayer.addEventListener('progress', () => {
                    if (typeof document.hidden !== "undefined" && document.hidden && canplaythroughTime != null) {
                        videoPlayer.currentTime = videoPlayer.buffered.end((videoPlayer.buffered.length - 1)) - canplaythroughTime;
                    }
                });
            
                videoPlayer.addEventListener('canplaythrough', () => {
                    if(canplaythroughTime == null){
                        canplaythroughTime = videoPlayer.buffered.end((videoPlayer.buffered.length - 1));
                    }
                });
            
                videoPlayer.addEventListener('loadeddata', () => {
                    videoPlayer.play();
                    //makePic();
                });
        
                videoPlayer.onerror = () => {
                    console.log("Error " + videoPlayer.error.code + "; details: " + videoPlayer.error.message);
                };
        
                startPlay();
            }
        }
    }, [playerReady]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div style={{ textAlign: isSmallDevice ? "left" : "center", display: isSmallDevice ? "flex" : "block", alignItems: "center", flex: 1 }}>
            <IconButton onClick={openDialog}>
                <LiveTvIcon style={{ color: "#18cc69" }} />
            </IconButton>

            <Dialog fullWidth open={dialogVisible} onClose={closeDialog} classes={{ paper: classes.fullWidthDialog }}>
                {record[source] &&
                    <video id={`videoPlayer${record.id}`} autoPlay controls muted playsInline></video>
                }
            </Dialog>
        </div>
    );
}